import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import {
  Box,
  Stack,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
} from '@mui/material';
import Iconify from '../../components/Iconify';
import AccountPopover from './AccountPopover';
import { HEADER, NAVBAR } from '../../config';
import cssStyles from '../../utils/cssStyles';
import Breadcrumbs from '../../components/Breadcrumbs';
import { useEffect, useState, useCallback } from 'react';
import { useAppSelector } from '../../store';
import NotificationDropdown from '../../components/NotificationDropdown';
import { fetchUnreadCount } from '../../services/advertise-edit-notification';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchLicenseExpirationAlert, LicenseExpirationAlert } from '../../services/license-expiration-alert';

const APPBAR_MOBILE = 64;
const APPBAR_DESKTOP = 92;

const RootStyle = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'isCollapse',
})(({ theme, isCollapse }: { theme?: any; isCollapse: boolean }) => ({
  ...cssStyles(theme).bgBlur({}),
  boxShadow: 'none',
  height: HEADER.MOBILE_HEIGHT,
  zIndex: theme.zIndex.appBar + 1,
  transition: theme.transitions.create(['width', 'height'], {
    duration: theme.transitions.duration.shorter,
  }),
  [theme.breakpoints.up('lg')]: {
    height: HEADER.DASHBOARD_DESKTOP_HEIGHT,
    width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH + 1}px)`,
    ...(isCollapse && {
      width: `calc(100% - ${NAVBAR.DASHBOARD_COLLAPSE_WIDTH}px)`,
    }),
  },
}));

const ToolbarStyle = styled(Toolbar)(({ theme }) => ({
  minHeight: APPBAR_MOBILE,
  [theme.breakpoints.up('lg')]: {
    minHeight: APPBAR_DESKTOP,
    padding: theme.spacing(0, 5),
  },
}));

const DashboardNavbar = ({
  onOpenSidebar,
  isCollapse,
}: {
  onOpenSidebar: any;
  isCollapse: boolean;
}) => {
  const userProfile = useAppSelector((state) => state.auth.user);
  const userRole = useAppSelector((state) => state.auth.user.userRole);
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const companyId = useAppSelector((state) => state.auth.user.id);
  const navigate = useNavigate();
  const location = useLocation();
  const [title, setTitle] = useState({
    title: '',
    links: [{ name: '', href: '' }],
  });
  const [unreadCount, setUnreadCount] = useState(0);
  const [licenseAlert, setLicenseAlert] = useState<LicenseExpirationAlert | null>(null);

  // Fetch unread count
  const loadUnreadCount = useCallback(async () => {
    if (!accessToken) return;
    
    try {
      const response = await fetchUnreadCount();
      setUnreadCount(response.count || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, [accessToken]);

  // Poll for unread count every 30 seconds
  useEffect(() => {
    if (!accessToken) return;

    // Load immediately
    loadUnreadCount();

    // Set up polling interval
    const interval = setInterval(() => {
      loadUnreadCount();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [accessToken, loadUnreadCount]);

  // Fetch license expiration alerts (for company users only)
  useEffect(() => {
    const fetchLicenseAlerts = async () => {
      if (!companyId || userRole !== 'company') return;

      try {
        const response = await fetchLicenseExpirationAlert();

        if (response.hasAlert && response.alert) {
          setLicenseAlert(response.alert);
        } else {
          setLicenseAlert(null);
        }
      } catch (error) {
        console.error('Error fetching license alerts:', error);
        setLicenseAlert(null);
      }
    };

    fetchLicenseAlerts();
  }, [companyId, userRole]);

  useEffect(() => {
    const path = location.pathname;
    if (path) {
      if (path === '/') {
        setTitle({
          title: 'Dashboard',
          links: [{ name: '', href: '' }],
        });
      } else if (path === '/company-dashboard') {
        setTitle({
          title: 'Dashboard',
          links: [{ name: '', href: '' }],
        });
      } else if (path === '/users') {
        setTitle({
          title: 'Users',
          links: [{ name: 'Dashboard', href: '/' }],
        });
      } else if (path === '/property-category') {
        setTitle({
          title: 'Property Category',
          links: [{ name: 'Dashboard', href: '/' }],
        });
      } else if (path === '/property-sub-category') {
        setTitle({
          title: 'Property Sub Category',
          links: [{ name: 'Dashboard', href: '/' }],
        });
      } else if (path === '/property-sub-category/create') {
        setTitle({
          title: 'Add Property Sub Category',
          links: [
            { name: 'Dashboard', href: '/' },
            { name: 'Property Sub Category', href: '/property-sub-category' },
          ],
        });
      } else if (path.startsWith('/property-sub-category/edit/')) {
        setTitle({
          title: 'Edit Property Sub Category',
          links: [
            { name: 'Dashboard', href: '/' },
            { name: 'Property Sub Category', href: '/property-sub-category' },
          ],
        });
      } else if (path === '/property-feature') {
        setTitle({
          title: 'Property Feature',
          links: [{ name: 'Dashboard', href: '/' }],
        });
      } else if (path === '/property-feature/create') {
        setTitle({
          title: 'Add Property Feature',
          links: [
            { name: 'Dashboard', href: '/' },
            { name: 'Property Feature', href: '/property-feature' },
          ],
        });
      } else if (path.startsWith('/property-feature/edit/')) {
        setTitle({
          title: 'Edit Property Feature',
          links: [
            { name: 'Dashboard', href: '/' },
            { name: 'Property Feature', href: '/property-feature' },
          ],
        });
      } else if (path === '/property-facility') {
        setTitle({
          title: 'Property Facility',
          links: [{ name: 'Dashboard', href: '/' }],
        });
      } else if (path === '/property-facility/create') {
        setTitle({
          title: 'Add Property Facility',
          links: [
            { name: 'Dashboard', href: '/' },
            { name: 'Property Facility', href: '/property-facility' },
          ],
        });
      } else if (path.startsWith('/property-facility/edit/')) {
        setTitle({
          title: 'Edit Property Facility',
          links: [
            { name: 'Dashboard', href: '/' },
            { name: 'Property Facility', href: '/property-facility' },
          ],
        });
      } else if (path === '/location-management') {
        setTitle({
          title: 'Location Management',
          links: [{ name: 'Dashboard', href: '/' }],
        });
      } else if (path === '/location-management/create') {
        setTitle({
          title: 'Add Location Management',
          links: [
            { name: 'Dashboard', href: '/' },
            { name: 'Location Management', href: '/location-management' },
          ],
        });
      } else if (path.startsWith('/location-management/edit/')) {
        setTitle({
          title: 'Edit Location Management',
          links: [
            { name: 'Dashboard', href: '/' },
            { name: 'Location Management', href: '/location-management' },
          ],
        });
      } else if (path === '/advertise-package') {
        setTitle({
          title: 'Advertise Package',
          links: [{ name: 'Dashboard', href: '/' }],
        });
      } else if (path === '/advertise') {
        setTitle({
          title: 'Advertise',
          links: [{ name: 'Dashboard', href: '/' }],
        });
      } else if (path.startsWith('/advertise/view/')) {
        setTitle({
          title: 'View Advertise',
          links: [
            { name: 'Dashboard', href: '/' },
            { name: 'Advertise', href: '/advertise' },
          ],
        });
      } else if (path === '/settings') {
        setTitle({
          title: 'Settings',
          links: [{ name: 'Dashboard', href: '/' }],
        });
      } else if (path === '/contact-form') {
        setTitle({
          title: 'Contact Form',
          links: [{ name: 'Dashboard', href: '/' }],
        });
      } else if (path === '/user/profile') {
        const dashboardLink = userRole === 'company' 
          ? { name: 'Company Dashboard', href: '/company-dashboard' }
          : { name: 'Dashboard', href: '/' };
        setTitle({
          title: 'Profile',
          links: [dashboardLink],
        });
      } else if (path === '/profile') {
        if (userRole === 'company') {
          setTitle({
            title: '',
            links: [],
          });
        } else {
        setTitle({
          title: 'Update Profile',
          links: [
            { name: 'Dashboard', href: '/' },
            {
              name: `${userProfile.name}`,
              href: '/user/profile',
            },
          ],
        });
        }
      } else if (path === '/notification') {
        setTitle({
          title: 'Notification',
          links: [{ name: 'Dashboard', href: '/' }],
        });
      } else if (path === '/payment') {
        setTitle({
          title: 'Payment',
          links: [{ name: 'Dashboard', href: '/' }],
        });
      } else if (path.startsWith('/kpi')) {
        setTitle({
          title: 'KPI Dashboard',
          links: [{ name: 'Dashboard', href: '/' }],
        });
      } else if (path === '/advertise/create') {
        setTitle({
          title: 'Add advertise',
          links: [
            { name: 'Dashboard', href: '/' },
            { name: 'Advertise', href: '/advertise' },
          ],
        });
      } else if (path.startsWith('/property-sub-category/view/')) {
        setTitle({
          title: 'View Property Sub Category',
          links: [
            { name: 'Dashboard', href: '/' },
            { name: 'Property Sub Category', href: '/property-sub-category' },
          ],
        });
      }
    }
  }, [location.pathname, userRole, userProfile.name]);

  return (
    <RootStyle isCollapse={isCollapse}>
      <ToolbarStyle>
        <IconButton
          onClick={onOpenSidebar}
          sx={{ mr: 1, color: 'text.primary', display: { lg: 'none' } }}
        >
          <Iconify icon='eva:menu-2-fill' />
        </IconButton>

        <Box sx={{ flexGrow: 1 }}>
          {/* Special layout for /user/profile and /company-dashboard paths with company admin - title first, then alert */}
          {(location.pathname === '/user/profile' || location.pathname === '/company-dashboard') && userRole === 'company' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pb: 2 }}>
              {/* Title and Breadcrumbs - Show at top */}
              {title?.title && (
              <Box>
                <Typography
                  variant='h4'
                  sx={{ color: '#000', lineHeight: 1.2 }}
                >
                  {title?.title}
                </Typography>
                <Breadcrumbs links={title?.links} />
              </Box>
              )}
              {/* License Expiration Alert - Show below title */}
              {licenseAlert && (
                <Typography
                  variant="body2"
                  onClick={() => navigate('/license-renewal')}
                  sx={{
                    color: 'error.main',
                    fontSize: '0.875rem',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontWeight: 500,
                    cursor: 'pointer',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  <Iconify icon="eva:alert-triangle-fill" width={16} sx={{ color: 'error.main' }} />
                  {licenseAlert.isExpired ? (
                    `Broker License expired on ${licenseAlert.expirationDate || 'N/A'}. You have ${licenseAlert.gracePeriodDaysRemaining || 0} days remaining in grace period. Please upload renewed license immediately to avoid account suspension.`
                  ) : (
                    `Broker License expires in ${licenseAlert.daysRemaining || 0} days on ${licenseAlert.expirationDate || 'N/A'}. Please upload renewed license to avoid account suspension.`
                  )}
                </Typography>
              )}
            </Box>
          ) : (
            /* Default layout for all other pages - horizontal layout */
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* License Expiration Alert - Show for company users on left side */}
            {userRole === 'company' && licenseAlert && (
              <Typography
                variant="body2"
                onClick={() => navigate('/license-renewal')}
                sx={{
                  color: 'error.main',
                  fontSize: '0.875rem',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontWeight: 500,
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                <Iconify icon="eva:alert-triangle-fill" width={16} sx={{ color: 'error.main' }} />
                {licenseAlert.isExpired ? (
                  `Broker License expired on ${licenseAlert.expirationDate || 'N/A'}. You have ${licenseAlert.gracePeriodDaysRemaining || 0} days remaining in grace period. Please upload renewed license immediately to avoid account suspension.`
                ) : (
                  `Broker License expires in ${licenseAlert.daysRemaining || 0} days on ${licenseAlert.expirationDate || 'N/A'}. Please upload renewed license to avoid account suspension.`
                )}
              </Typography>
            )}
              {/* Hide title/breadcrumbs for company admin on pages other than /user/profile and /company-dashboard */}
              {title?.title && !(userRole === 'company' && location.pathname !== '/user/profile' && location.pathname !== '/company-dashboard') && (
            <Box sx={{ flexGrow: 1 }}>
              <Typography
                variant='h4'
                sx={{ color: '#000', lineHeight: 1.2 }}
              >
                {title?.title}
              </Typography>
              <Breadcrumbs links={title?.links} />
            </Box>
              )}
          </Box>
          )}
        </Box>

        <Stack
          direction='row'
          alignItems='center'
          spacing={{ xs: 0.5, sm: 1.5 }}
        >
          {/* Show notification icon only for company and admin roles */}
          {(userRole === 'company' || userRole === 'admin' || userRole === 'adminTier2') && (
            <NotificationDropdown
              unreadCount={unreadCount}
              onCountChange={setUnreadCount}
            />
          )}
          <AccountPopover />
        </Stack>
      </ToolbarStyle>
    </RootStyle>
  );
};

DashboardNavbar.propTypes = {
  onOpenSidebar: PropTypes.func,
};
export default DashboardNavbar;
